import { createSignal, createEffect, createMemo, onCleanup, For, Show } from 'solid-js';
import { lex, makeByteToChar } from '../lib/formulaLex.js';

// Whole-scheme document editor: a transparent <textarea> stacked on a <pre>
// that renders the colored tokens. Instant colors come from the mirrored JS
// lexer; authoritative errors (squiggles + message list) come, debounced,
// from POST /api/scheme/doc/check — the Go compiler is the single source of
// truth and documents are self-contained, so this check is the FULL compile.
//
// Props:
//   source()     — signal accessor for the document text
//   setSource(v) — setter
//   builtins()   — accessor: [{Name, Sig, Doc}] for autocomplete
//   onCheck(res) — receives the /api/scheme/doc/check response
//                  ({valid, errors, components}) or null while empty
//   ready(api)   — optional: called once with {scrollToLine(n)}

const CHECK_DEBOUNCE_MS = 300;

export default function SchemeDocEditor(props) {
  let taRef, preRef;
  const [errors, setErrors] = createSignal([]); // char-offset spans
  const [ac, setAc] = createSignal(null); // {items, index, replaceStart}

  // Line-wrap toggle (persisted). Off → white-space:pre on BOTH layers and a
  // horizontal scrollbar; the two layers stay glyph-identical so the caret holds.
  const [wrap, setWrap] = createSignal((() => {
    try { return localStorage.getItem('kg_editor_wrap') !== '0'; } catch { return true; }
  })());
  function toggleWrap() {
    const v = !wrap();
    setWrap(v);
    try { localStorage.setItem('kg_editor_wrap', v ? '1' : '0'); } catch { /* ignore */ }
  }

  // ── Authoritative server check, debounced ────────────────────────────
  let timer;
  createEffect(() => {
    const src = props.source();
    clearTimeout(timer);
    if (src.trim() === '') { setErrors([]); props.onCheck?.(null); return; }
    timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/scheme/doc/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: src }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (props.source() !== src) return; // stale — user kept typing
        const b2c = makeByteToChar(src);
        setErrors((data.errors ?? []).map(e => ({
          msg: e.msg, start: b2c(e.start), end: b2c(e.end),
        })));
        props.onCheck?.(data);
      } catch { /* server unreachable — keep last known state */ }
    }, CHECK_DEBOUNCE_MS);
  });
  onCleanup(() => clearTimeout(timer));

  // ── Highlight segments: local tokens + server error spans ────────────
  const segments = createMemo(() => {
    const src = props.source();
    const toks = lex(src);
    const errs = errors().map(e => ({
      start: Math.min(e.start, src.length),
      end: Math.min(Math.max(e.end, e.start + 1), src.length) || 0,
    }));
    const bounds = new Set([0, src.length]);
    for (const t of toks) { bounds.add(t.start); bounds.add(t.end); }
    for (const e of errs) { bounds.add(e.start); bounds.add(e.end); }
    const pts = [...bounds].sort((a, b) => a - b);
    const out = [];
    for (let k = 0; k + 1 < pts.length; k++) {
      const s = pts[k], e = pts[k + 1];
      if (s >= e) continue;
      const tok = toks.find(t => t.start <= s && e <= t.end);
      const err = errs.some(er => er.start <= s && s < er.end);
      out.push({
        text: src.slice(s, e),
        cls: (tok ? 'tok-' + tok.kind : '') + (err ? ' tok-err' : ''),
      });
    }
    return out;
  });

  // ── Autocomplete: builtins + component names declared ABOVE the caret ─
  function namesAbove(src, caret) {
    const lines = src.slice(0, caret).split('\n');
    lines.pop(); // the caret's own line is being typed
    const re = /^\s*(?:calc|slip\s*\(\s*[a-z]+\s*\))\s+([A-Za-z_][A-Za-z0-9_]*)/;
    const names = [];
    for (const ln of lines) {
      const m = ln.match(re);
      if (m) names.push(m[1]);
    }
    return names;
  }

  function updateAutocomplete() {
    const src = props.source();
    const caret = taRef.selectionStart;
    if (caret !== taRef.selectionEnd) { setAc(null); return; }
    const before = src.slice(0, caret);
    const ctx = before.match(/(?:^|[^A-Za-z0-9_"])([A-Za-z_][A-Za-z0-9_]*)$/);
    if (!ctx) { setAc(null); return; }
    const partial = ctx[1].toLowerCase();

    const comps = namesAbove(src, caret)
      .filter(n => n.toLowerCase().startsWith(partial) && n !== ctx[1])
      .map(n => ({ insert: n, label: n, hint: 'komponen di atas' }));
    const fns = (props.builtins?.() ?? [])
      .filter(b => b.Name.toLowerCase().startsWith(partial) && b.Name !== partial)
      .map(b => ({ insert: b.Name + '(', label: b.Sig, hint: b.Doc }));
    const items = [...comps, ...fns];
    setAc(items.length ? { items, index: 0, replaceStart: caret - ctx[1].length } : null);
  }

  function accept(item) {
    const a = ac();
    if (!a) return;
    const src = props.source();
    const caret = taRef.selectionStart;
    const next = src.slice(0, a.replaceStart) + item.insert + src.slice(caret);
    props.setSource(next);
    const pos = a.replaceStart + item.insert.length;
    setAc(null);
    queueMicrotask(() => { taRef.focus(); taRef.setSelectionRange(pos, pos); });
  }

  function onKeyDown(ev) {
    const a = ac();
    if (a) {
      if (ev.key === 'ArrowDown') { ev.preventDefault(); setAc({ ...a, index: (a.index + 1) % a.items.length }); return; }
      if (ev.key === 'ArrowUp') { ev.preventDefault(); setAc({ ...a, index: (a.index - 1 + a.items.length) % a.items.length }); return; }
      if (ev.key === 'Tab' || ev.key === 'Enter') { ev.preventDefault(); accept(a.items[a.index]); return; }
      if (ev.key === 'Escape') { ev.preventDefault(); setAc(null); return; }
    }
  }

  function syncScroll() {
    preRef.scrollTop = taRef.scrollTop;
    preRef.scrollLeft = taRef.scrollLeft;
  }

  // ── Outline navigation: scroll to a 1-based document line ────────────
  // The pre mirrors the textarea's layout exactly (including soft wraps),
  // so a collapsed Range at the line-start offset measures the true visual
  // position — robust even for long wrapped formula lines.
  function scrollToLine(line) {
    const src = props.source();
    let off = 0;
    for (let l = 1; l < line; l++) {
      const nl = src.indexOf('\n', off);
      if (nl < 0) break;
      off = nl + 1;
    }
    const walker = document.createTreeWalker(preRef, NodeFilter.SHOW_TEXT);
    let acc = 0, node;
    while ((node = walker.nextNode())) {
      const len = node.textContent.length;
      if (acc + len >= off) {
        const range = document.createRange();
        range.setStart(node, Math.min(off - acc, len));
        range.collapse(true);
        const rect = range.getBoundingClientRect();
        const preRect = preRef.getBoundingClientRect();
        taRef.scrollTop = preRef.scrollTop + (rect.top - preRect.top) - 60;
        syncScroll();
        break;
      }
      acc += len;
    }
    taRef.focus();
    taRef.setSelectionRange(off, off);
  }
  props.ready?.({ scrollToLine });

  return (
    <div class="feditor feditor-doc" classList={{ 'feditor-nowrap': !wrap() }}>
      <button
        class="feditor-wrap-toggle"
        type="button"
        title={wrap() ? 'Bungkus baris AKTIF — klik untuk matikan (scroll horizontal)' : 'Bungkus baris MATI — klik untuk aktifkan'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggleWrap}
      >
        {wrap() ? '↩ wrap: on' : '→ wrap: off'}
      </button>
      <pre ref={preRef} class="feditor-hl" aria-hidden="true"><For each={segments()}>{(s) =>
        s.cls ? <span class={s.cls}>{s.text}</span> : s.text
      }</For>{'\n'}</pre>
      <textarea
        ref={taRef}
        class="feditor-input"
        spellcheck={false}
        autocomplete="off"
        autocapitalize="off"
        value={props.source()}
        onInput={(e) => { props.setSource(e.currentTarget.value); updateAutocomplete(); }}
        onKeyDown={onKeyDown}
        onScroll={syncScroll}
        onClick={() => setAc(null)}
        onBlur={() => setTimeout(() => setAc(null), 150)}
      />
      <Show when={ac()}>
        <div class="ac-pop">
          <For each={ac().items.slice(0, 8)}>{(item, idx) =>
            <button
              class="ac-item"
              classList={{ active: idx() === ac().index }}
              onMouseDown={(e) => { e.preventDefault(); accept(item); }}
            >
              <span class="ac-label">{item.label}</span>
              <span class="ac-hint">{item.hint}</span>
            </button>
          }</For>
        </div>
      </Show>
      <Show when={errors().length > 0}>
        <div class="feditor-errors">
          <For each={errors()}>{(e) => <div class="ferr">⚠ {e.msg}</div>}</For>
        </div>
      </Show>
    </div>
  );
}
